/*
 * itlu-globo-gallery-sync.js
 *
 * When the customer interacts with a Globo Product Options control on the PDP,
 * jump the product gallery to the last media — which the merchant uses as
 * the Globo customisation preview image.
 *
 * Globo renders its UI inside `.gpo-container` and uses *bare* input names
 * (`Engraving Line 1`, `google-font-1`, …) for the visible controls. The
 * `properties[…]` inputs only appear as hidden bookkeeping that Globo writes
 * into at submit time, so we can't use them as the interaction signal.
 *
 * Instead we wait for `.gpo-container` to mount (Globo loads asynchronously
 * after page render) via a MutationObserver, then attach click/input/change
 * listeners directly to the container. This catches both native form-control
 * changes AND clicks on Globo's custom UI (color swatches, font picker, etc.).
 *
 * We only jump the gallery when the option set actually has personalization
 * enabled. Globo tags every personalize-enabled option element with a
 * `data-personalize` attribute (its own code gates the live-preview canvas on
 * `getAttribute("data-personalize")`), so if `.gpo-container` has no
 * `[data-personalize]` element there is no preview image to show and we leave
 * the gallery alone — otherwise ordinary options would hijack it too.
 *
 * The gallery is driven by a `theme:media:select` event dispatched on
 * <product-images> with detail.id matching the target slide's data-media-id.
 */
(function () {
  'use strict';

  if (window.__itluGloboGallerySync) return;
  window.__itluGloboGallerySync = true;

  const GLOBO_CONTAINER_SELECTOR = '.gpo-container';
  const PERSONALIZE_SELECTOR = '[data-personalize]';

  // Globo renders personalize-enabled option elements with a `data-personalize`
  // attribute. Its absence means this option set has no personalizer/preview,
  // so there is no reason to snap the gallery to the preview slide.
  function hasPersonalization(container) {
    return !!container.querySelector(PERSONALIZE_SELECTOR);
  }

  function navigateToLastMedia(productInfo) {
    const productImages = productInfo.querySelector('product-images');
    if (!productImages) return;

    const mediaList = productImages.querySelector('[data-product-media-list]');
    if (!mediaList) return;

    const slides = mediaList.querySelectorAll('[data-image-id]');
    if (slides.length < 2) return;

    const targetMediaId = slides[slides.length - 1].getAttribute('data-media-id');
    if (!targetMediaId) return;

    // Already on the preview slide.
    if (productImages.getAttribute('data-active-media') === targetMediaId) {
      return;
    }

    /**
     * ------------------------------------------------------------------
     * NEW IMPLEMENTATION
     * ------------------------------------------------------------------
     * Simulate the exact user interaction by clicking the thumbnail link.
     * Globo listens to thumbnail clicks to refresh the personalizer.
     * ------------------------------------------------------------------
     */
    const thumbLink = productImages.querySelector(
      `[data-thumb-link][data-media-id="${targetMediaId}"]`
    );

    if (thumbLink) {
      thumbLink.click();
      return;
    }

    /**
     * ------------------------------------------------------------------
     * OLD IMPLEMENTATION (fallback / reference)
     * ------------------------------------------------------------------
     */

    /*
    productImages.dispatchEvent(
      new CustomEvent('theme:media:select', {
        bubbles: true,
        detail: { id: targetMediaId }
      })
    );
    */
  }

  function bindGloboContainer(container) {
    if (container.__itluGloboBound) return;
    container.__itluGloboBound = true;

    const productInfo = container.closest('product-info');
    if (!productInfo) return;

    // Checked at fire-time (not bind-time): Globo may still be rendering its
    // fields when the container first mounts, and it re-renders on variant swap.
    const fire = () => {
      if (!hasPersonalization(container)) return;
      navigateToLastMedia(productInfo);
    };
    container.addEventListener('click', fire);
    container.addEventListener('input', fire);
    container.addEventListener('change', fire);
  }

  function scanAndBind(root) {
    if (root instanceof Element && root.matches && root.matches(GLOBO_CONTAINER_SELECTOR)) {
      bindGloboContainer(root);
    }
    if (root.querySelectorAll) {
      root.querySelectorAll(GLOBO_CONTAINER_SELECTOR).forEach(bindGloboContainer);
    }
  }

  function start() {
    scanAndBind(document);

    // Globo mounts asynchronously and re-mounts after variant-swap view
    // transitions, so watch the whole body for new `.gpo-container` nodes.
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) scanAndBind(node);
        });
      }
    });
    observer.observe(document.body, { subtree: true, childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
